import { useState, useEffect } from 'react';
import api from '../utils/api';
import '../styles/CreateNewAdmin.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CreateNewAdmin from './CreateNewAdmin';
import logger from '../utils/logger';
import { useTenant } from '../context/TenantContext';

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { tenant } = useTenant();
  const clinicName = tenant?.brandName || tenant?.name || 'this clinic';

  
  // Edit form state
  const [editForm, setEditForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'viewer',
    accountType: 'clinic',
    permissions: {
      canAddModule: false,
      canDeleteModule: false,
      canRenameModule: false,
      canEditModule: false,
      canImportModule: false,
      canExportModule: false,
      canReorderModules: false,
    }
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  // Predefined Quick Roles
  const quickRoles = {
    admin: {
      label: 'Full Access',
      permissions: {
        canAddModule: true,
        canDeleteModule: true,
        canRenameModule: true,
        canEditModule: true,
        canImportModule: true,
        canExportModule: true,
        canReorderModules: true,
      }
    },
    editor: {
      label: 'Editor',
      permissions: {
        canAddModule: true,
        canDeleteModule: false,
        canRenameModule: true,
        canEditModule: true,
        canImportModule: true,
        canExportModule: true,
        canReorderModules: true,
      }
    },
    viewer: {
      label: 'View Only',
      permissions: {
        canAddModule: false,
        canDeleteModule: false,
        canRenameModule: false,
        canEditModule: false,
        canImportModule: false,
        canExportModule: true,
        canReorderModules: false,
      }
    }
  };

  // Permission labels for display
  const permissionLabels = {
    canAddModule: 'Add Module',
    canDeleteModule: 'Delete Module',
    canRenameModule: 'Rename Module',
    canEditModule: 'Edit Module',
    canImportModule: 'Import Module',
    canExportModule: 'Export Module',
    canReorderModules: 'Reorder Modules',
  };

  // Fetch Admins
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const [res, superRes] = await Promise.all([
        api.get('/api/admins'),
        // Super admins have access to every clinic, so they're listed with each clinic's admins.
        api.get('/api/super-admins').catch((err) => {
          logger.error('Error fetching super admins:', err);
          return { data: { superAdmins: [] } };
        }),
      ]);
      setAdmins(res.data.admins || []);
      setSuperAdmins(superRes.data.superAdmins || []);
    } catch (error) {
      logger.error('Error fetching admins:', error);
      // Don't show alert for 401/403 - api interceptor handles redirect
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        alert('Failed to load admins');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Open Edit Modal (super admins are passed with superAdmin: true)
  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setEditForm({
      email: admin.email,
      password: '',
      confirmPassword: '',
      role: admin.superAdmin ? 'admin' : (admin.role || 'viewer'),
      accountType: admin.superAdmin ? 'super' : 'clinic',
      permissions: admin.permissions || quickRoles.viewer.permissions
    });
    setShowEditModal(true);
    setFormError('');
    setShowPassword(false);
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedAdmin(null);
    setEditForm({
      email: '',
      password: '',
      confirmPassword: '',
      role: 'viewer',
      accountType: 'clinic',
      permissions: quickRoles.viewer.permissions
    });
    setFormError('');
  };

  // Handle Quick Role Change
  const handleQuickRoleChange = (role) => {
    setEditForm({
      ...editForm,
      role,
      permissions: { ...quickRoles[role].permissions }
    });
  };

  // Toggle Individual Permission
  const togglePermission = (permission) => {
    setEditForm({
      ...editForm,
      permissions: {
        ...editForm.permissions,
        [permission]: !editForm.permissions[permission]
      }
    });
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    // Validate email
    if (!editForm.email.trim()) {
      setFormError('Email is required');
      return;
    }

    // Validate password if changing
    if (editForm.password) {
      if (editForm.password !== editForm.confirmPassword) {
        setFormError('Passwords do not match');
        return;
      }
    }

    const wasSuper = !!selectedAdmin.superAdmin;
    const toSuper = editForm.accountType === 'super';
    if (toSuper !== wasSuper && !window.confirm(toSuper
      ? `Upgrade ${editForm.email} to super admin? They get access to every clinic and this console, keep their password, and their clinic admin accounts are replaced.`
      : `Downgrade ${editForm.email} to a clinic admin of ${clinicName}? They lose access to every other clinic and this console.`)) {
      return;
    }

    try {
      const payload = {
        email: editForm.email,
        permissions: editForm.permissions
      };
      if (!wasSuper) payload.role = editForm.role;

      // Only include password if it's being changed
      if (editForm.password) {
        payload.password = editForm.password;
      }

      // Save the details on the current account first, then change its tier (the password carries over).
      if (wasSuper) {
        await api.put(`/api/super-admins/${selectedAdmin.id}`, payload);
        if (!toSuper) {
          await api.post(`/api/super-admins/${selectedAdmin.id}/demote`, { role: editForm.role, permissions: editForm.permissions });
        }
      } else {
        await api.put(`/api/admin/${selectedAdmin.id}`, payload);
        if (toSuper) {
          await api.post(`/api/super-admins/promote/${selectedAdmin.id}`, { permissions: editForm.permissions });
        }
      }

      alert('Administrator updated successfully');
      closeEditModal();
      fetchAdmins();
    } catch (error) {
      logger.error('Error updating admin:', error);
      setFormError(error.response?.data?.message || error.response?.data?.error || 'Failed to update administrator');
      fetchAdmins();
    }
  };

  // Delete Modal Functions
  const openDeleteModal = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      await api.delete(adminToDelete.superAdmin ? `/api/super-admins/${adminToDelete.id}` : `/api/admin/${adminToDelete.id}`);
      alert('Admin deleted successfully');
      closeDeleteModal();
      fetchAdmins();
    } catch (error) {
      logger.error('Error deleting admin:', error);
      alert(error.response?.data?.message || 'Failed to delete admin');
    }
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin', color: '#4CAF50' },
      editor: { label: 'Editor', color: '#2196F3' },
      viewer: { label: 'Viewer', color: '#FF9800' }
    };
    return badges[role] || badges.viewer;
  };

  return (
    <div style={{ margin: '50px' }}>
       <div className="create-new-btn-container">
        <button 
          className="create-new-btn"  
          onClick={() => setShowCreateForm(true)}
        >
          <PersonIcon />
          Create New Admin
        </button>
      </div>
      {/* Admins Table */}
      <div className='admin-table-container'>
        <table className='admin-table'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Permissions</th>
              <th className='delete-row'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan='6' style={{ textAlign: 'center' }}>
                  ⏳ Loading admins...
                </td>
              </tr>
            ) : admins.length === 0 && superAdmins.length === 0 ? (
              <tr>
                <td colSpan='6' style={{ textAlign: 'center' }}>
                  No admins found
                </td>
              </tr>
            ) : (
              <>
              {superAdmins.map((admin) => {
                const name = admin.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                // The server refuses removing yourself or the last super admin.
                const canDelete = !admin.you && superAdmins.length > 1;
                return (
                  <tr key={`super-${admin.id}`}>
                    <td style={{ fontWeight: 500 }}>
                      {name}
                      {admin.you && <span style={{ marginLeft: '6px', fontSize: '12px', color: '#888', fontWeight: 400 }}>(you)</span>}
                    </td>
                    <td>{admin.email}</td>
                    <td>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: '#7B1FA2',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Super Admin
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Active
                      </span>
                    </td>
                    <td>
                      {Object.values(admin.permissions || {}).every(Boolean) ? (
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          backgroundColor: '#f3e5f5',
                          color: '#7B1FA2',
                          borderRadius: '8px'
                        }}>
                          Full access, all clinics
                        </span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {Object.entries(admin.permissions)
                            .filter(([, value]) => !value)
                            .map(([key]) => (
                              <span key={key} style={{
                                fontSize: '11px',
                                padding: '2px 8px',
                                backgroundColor: '#fdecea',
                                color: '#c62828',
                                borderRadius: '8px'
                              }}>
                                No {permissionLabels[key]}
                              </span>
                            ))}
                        </div>
                      )}
                    </td>
                    <td className='d-flex'>
                      <button
                        onClick={() => openEditModal({ ...admin, superAdmin: true })}
                        className='edit-btn'
                      >
                        <EditIcon />
                      </button>
                      {canDelete && (
                        <button
                          className='delete-btn'
                          onClick={() => openDeleteModal({ ...admin, superAdmin: true })}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {admins.map((admin) => {
                const badge = getRoleBadge(admin.role);
                const name = admin.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                return (
                  <tr key={admin.id}>
                    <td style={{ fontWeight: 500 }}>{name}</td>
                    <td>{admin.email}</td>
                    <td>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: badge.color,
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Active
                      </span>
                    </td>
                    <td>
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
    {Object.values(admin.permissions || {}).filter(v => v).length === 0 ? (
      // 👉 No permissions → show N/A
      <span style={{
        fontSize: '11px',
        padding: '2px 8px',
        backgroundColor: '#f5f5f5',
        color: '#999',
        borderRadius: '8px'
      }}>
        N/A
      </span>
    ) : (
      <>
        {Object.entries(admin.permissions || {})
          .filter(([_, value]) => value)
          .slice(0, 3)
          .map(([key]) => (
            <span key={key} style={{
              fontSize: '11px',
              padding: '2px 8px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              borderRadius: '8px'
            }}>
              {permissionLabels[key]}
            </span>
          ))}

        {Object.values(admin.permissions || {}).filter(v => v).length > 3 && (
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            backgroundColor: '#f5f5f5',
            color: '#666',
            borderRadius: '8px'
          }}>
            +{Object.values(admin.permissions || {}).filter(v => v).length - 3} more
          </span>
        )}
      </>
    )}
  </div>
</td>

                    <td className='d-flex'>
                      <button
                        onClick={() => openEditModal(admin)}
                        className='edit-btn'
                      >
                        <EditIcon />
                      </button>
                      <button
                        className='delete-btn'
                        onClick={() => openDeleteModal(admin)}
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Administrator Modal */}
      {showEditModal && (
        <div className='modal-overlay'>
          <div className='edit-admin-modal'>
            {/* Header */}
            <div className='modal-header'>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <EditIcon style={{ color: 'white' }} />
                <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>
                  Edit Administrator
                </h2>
              </div>
              <button
                onClick={closeEditModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <CloseIcon />
              </button>
            </div>

            <div className='modal-body'>
              {/* Basic Details Section */}
              <div className='form-section'>
                <h3 className='section-title'>Basic Details</h3>
                
                <div className='form-group'>
                  <label>Email</label>
                  <input
                    type='email'
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder='user@dntech.com'
                    className='form-input'
                  />
                </div>

                <div className='form-group'>
                  <label>Password (Leave blank to keep current)</label>
                  <div className='password-input-wrapper'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      placeholder='Enter new password'
                      className='form-input'
                      autoComplete="off" 
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='show-password-btn'
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                {editForm.password && (
                  <div className='form-group'>
                    <label>Confirm New Password</label>
                    <input
                      type='password'
                      value={editForm.confirmPassword}
                      onChange={(e) => setEditForm({ ...editForm, confirmPassword: e.target.value })}
                      placeholder='Confirm new password'
                      className='form-input'
                    />
                  </div>
                )}
              </div>

              {/* Access & Permissions Section */}
              <div className='form-section'>
                <h3 className='section-title'>Access & Permissions</h3>

                {/* Account tier. You can't downgrade yourself or the last super admin. */}
                <div className='form-group'>
                  <label>Account type</label>
                  <select
                    value={editForm.accountType}
                    onChange={(e) => setEditForm({ ...editForm, accountType: e.target.value })}
                    className='form-select'
                    disabled={selectedAdmin?.superAdmin && (selectedAdmin.you || superAdmins.length <= 1)}
                  >
                    <option value='clinic'>Clinic admin for {clinicName}</option>
                    <option value='super'>Super admin (all clinics)</option>
                  </select>
                </div>

                {/* Quick Role Selector (clinic admins only) */}
                {editForm.accountType === 'clinic' && (
                <div className='form-group'>
                  <label>Quick Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => handleQuickRoleChange(e.target.value)}
                    className='form-select'
                  >
                    <option value='admin'>Full Access (Admin)</option>
                    <option value='editor'>Editor</option>
                    <option value='viewer'>View Only (Viewer)</option>
                  </select>
                </div>
                )}

                {/* Permissions Table */}
                <div className='permissions-table'>
                  <div className='permissions-header'>
                    <div className='permission-col'>Module</div>
                    <div className='permission-col'>Access</div>
                  </div>

                  {Object.entries(permissionLabels).map(([key, label]) => (
                    <div key={key} className='permission-row'>
                      <div className='permission-label'>{label}</div>
                      <div className='permission-toggle'>
                        <label className='toggle-switch'>
                          <input
                            type='checkbox'
                            checked={editForm.permissions[key]}
                            onChange={() => togglePermission(key)}
                          />
                          <span className='toggle-slider'></span>
                        </label>
                        <span className='toggle-status'>
                          {editForm.permissions[key] ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {formError && (
                <div className='error-message'>
                  {formError}
                </div>
              )}

              {/* Action Buttons */}
              <div className='modal-actions'>
                <button
                  onClick={closeEditModal}
                  className='btn-cancel-modal'
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className='btn-save-admin'
                >
                  Save Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className='modal-overlay'>
          <div className='modal-content'>
            <h2>Delete Admin</h2>
            <p>Are you sure you want to delete this admin?</p>
            <p className='admin-email-highlight'>{adminToDelete?.email}</p>
            <div className='modal-actions'>
              <button className='btn-cancel' onClick={closeDeleteModal}>
                Cancel
              </button>
              <button className='btn-confirm-delete' onClick={confirmDeleteAdmin}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Admin Modal */}
      {showCreateForm && (
        <CreateNewAdmin
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => fetchAdmins()}
        />
      )}
    </div>
  );
};

export default AdminList;